import React, { useState, useEffect } from 'react';
import { Spin, Progress, Card, Typography, Space, Button, Tag } from 'antd';
import {
  CloudSyncOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  WifiOutlined,
  DisconnectOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;

const DataPreloadIndicator = ({
  isPreloading,
  preloadProgress,
  preloadError,
  cacheStatus,
  onRetry,
  isOnline,
  language = 'en'
}) => {
  const [showInitialLoad, setShowInitialLoad] = useState(true);
  const [hasShownCachedNotification, setHasShownCachedNotification] = useState(false);

  const getLocalizedText = (enText, hausaText) => language === 'en' ? enText : hausaText;

  // Hide the initial load indicator after 3 seconds
  useEffect(() => {
    if (!isPreloading && !preloadError) {
      const timer = setTimeout(() => {
        setShowInitialLoad(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isPreloading, preloadError]);

  // Only show cached notification once
  useEffect(() => {
    if (cacheStatus && !hasShownCachedNotification) {
      const allCached = cacheStatus.phcs === 'cached' &&
                     Object.values(cacheStatus.questions).every(status => status === 'cached');
      
      if (allCached) {
        setHasShownCachedNotification(true);
        // Data is cached successfully
      }
    }
  }, [cacheStatus, hasShownCachedNotification]);

  // Don't show any UI after initial load is complete
  if (!showInitialLoad && !isPreloading && !preloadError) {
    return null;
  }

  if (isPreloading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        backdropFilter: 'blur(4px)'
      }}>
        <Card 
          style={{
            width: '90%',
            maxWidth: 400,
            textAlign: 'center',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }}
          bodyStyle={{ padding: '32px 24px' }}
        >
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div>
              <CloudSyncOutlined 
                spin 
                style={{ 
                  fontSize: '48px', 
                  color: '#1890ff',
                  marginBottom: '16px'
                }} 
              />
              <Title level={4} style={{ margin: 0, color: '#1890ff' }}>
                {getLocalizedText('Loading Survey Data', 'Ana Loda Bayanan Bincike')}
              </Title>
              <Text type="secondary">
                {getLocalizedText(
                  'Preparing questions and health centers for offline use...',
                  'Ana shirya tambayoyi da cibiyoyin lafiya don amfani na kan layi...'
                )}
              </Text>
            </div>

            <div>
              <Progress 
                percent={preloadProgress} 
                status="active"
                strokeColor={{
                  '0%': '#108ee9',
                  '100%': '#87d068',
                }}
                showInfo={false}
                style={{ marginBottom: '8px' }}
              />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {getLocalizedText(
                  `Loading... ${Math.round(preloadProgress)}%`,
                  `Ana loda... ${Math.round(preloadProgress)}%`
                )}
              </Text>
            </div>

            <div>
              <Tag 
                icon={isOnline ? <WifiOutlined /> : <DisconnectOutlined />}
                color={isOnline ? 'success' : 'default'}
              >
                {isOnline 
                  ? getLocalizedText('Online', 'Akan layi')
                  : getLocalizedText('Offline', 'A kan layi')
                }
              </Tag>
            </div>
          </Space>
        </Card>
      </div>
    );
  }

  if (preloadError) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        backdropFilter: 'blur(4px)'
      }}>
        <Card 
          style={{
            width: '90%',
            maxWidth: 400,
            textAlign: 'center',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }}
          bodyStyle={{ padding: '32px 24px' }}
        >
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div>
              <ExclamationCircleOutlined 
                style={{ 
                  fontSize: '48px', 
                  color: '#ff4d4f',
                  marginBottom: '16px'
                }} 
              />
              <Title level={4} style={{ margin: 0, color: '#ff4d4f' }}>
                {getLocalizedText('Loading Failed', 'Loda Ta Gaza')}
              </Title>
              <Text type="secondary">
                {getLocalizedText(
                  'Failed to load survey data. Please check your connection and try again.',
                  'An kasa loda bayanan bincike. Da fatan za a gina sadarwarka ka sake gwabawa.'
                )}
              </Text>
            </div>

            <Button 
              type="primary" 
              icon={<ReloadOutlined />}
              onClick={onRetry}
              size="large"
              style={{
                borderRadius: '8px',
                height: '48px',
                fontWeight: '600'
              }}
            >
              {getLocalizedText('Try Again', 'Sake Gwabawa')}
            </Button>

            <div>
              <Tag 
                icon={isOnline ? <WifiOutlined /> : <DisconnectOutlined />}
                color={isOnline ? 'success' : 'default'}
              >
                {isOnline 
                  ? getLocalizedText('Online', 'Akan layi')
                  : getLocalizedText('Offline', 'A kan layi')
                }
              </Tag>
            </div>
          </Space>
        </Card>
      </div>
    );
  }

  return null;
};

export default DataPreloadIndicator;